/**
 * CatchballBall — First-class governance object
 *
 * A Ball represents one organizational branch's participation in a
 * Catchball Loop. It is the thing that travels: downward as a Request,
 * upward as a Proposal.
 *
 * Canon: docs/canon-addendum-ball-tree-split-aggregate.md
 * Spec:  User story §2 — Ball is distinct from the Proposal it carries.
 */

import type { BALL_WAITING_STATE__VALUES } from '../../generated/conceptRegisters';
import { parseCalendarDate } from '../../lib/calendarDate';

// ── Lifecycle states ──────────────────────────────────────────────────────────

export type BallStatus =
  | 'draft'                 // Created but not sent
  | 'sent'                  // Sent downward to receiving branch
  | 'opened'                // Receiving branch acknowledged
  | 'gathering_input'       // Branch collecting contributor inputs
  | 'in_local_deliberation' // Branch reviewing / debating / scoring
  | 'proposal_drafted'      // Proposal exists but not yet submitted
  | 'proposal_returned'     // Proposal submitted upward
  | 'under_review'          // Sending authority reviewing returned proposal
  | 'accepted'              // Proposal accepted as submitted
  | 'accepted_provisional'  // Accepted pending further reconciliation
  | 'returned_for_revision' // Returned with questions / change request
  | 'reconciled'            // Incorporated into higher-level reconciliation
  | 'closed';               // Ball's role in the loop is complete

/** Movement direction the Ball is currently in */
export type BallDirection =
  | 'downward'   // Request traveling to branch
  | 'upward'     // Proposal returning to executive
  | 'returned'   // Back downward for revision
  | 'closed';    // Complete

// ── Core Ball interface ───────────────────────────────────────────────────────

export interface CatchballBall {
  id: string;
  loopId: string;
  cycleId: string | null;
  workspaceId: string | null;

  // Identity
  name: string;                       // "Product Ball", "Engineering Ball"
  recipientOrgUnitId: string | null;
  recipientOrgUnitName: string | null;
  recipientUserId: string | null;
  recipientUserName: string | null;

  // Request payload (downward)
  requestText: string | null;
  requestInstructions: string | null;
  requestConstraints: string | null;
  requestEvidenceNotes: string | null;
  dueDate: string | null;

  // Lifecycle
  ballStatus: BallStatus;

  // Tree structure
  parentBallId: string | null;
  rootBallId: string | null;
  depth: number;
  /** When Aggregate last merged children into this parent (migration 620). */
  rollupAggregatedAt: string | null;

  // Track reference
  trackId: string | null;

  // Payload snapshots
  receivedContent: Record<string, unknown> | null;
  passedContent: Record<string, unknown> | null;

  // Exchange timestamps
  sentAt: string | null;
  sentByUserId: string | null;
  openedAt: string | null;
  proposalSubmittedAt: string | null;
  proposalAcceptedAt: string | null;
  lastActionAt: string;

  sequenceInLoop: number;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;

  // From view: denormalized loop/cycle context
  // (cycleId is declared with the core fields above as string | null)
  loopState?: string;
  loopIntent?: unknown;
}

// ── Derived helpers ───────────────────────────────────────────────────────────

/** Human-readable label for a Ball status */
export const BALL_STATUS_LABELS: Record<BallStatus, string> = {
  draft:                  'Draft',
  sent:                   'Sent',
  opened:                 'Opened',
  gathering_input:        'Gathering Input',
  in_local_deliberation:  'In Local Deliberation',
  proposal_drafted:       'Proposal Drafted',
  proposal_returned:      'Proposal Returned',
  under_review:           'Under Review',
  accepted:               'Accepted',
  accepted_provisional:   'Accepted Provisional',
  returned_for_revision:  'Returned for Revision',
  reconciled:             'Reconciled',
  closed:                 'Closed',
};

/** Which statuses represent a Ball with an upward-facing proposal */
export const PROPOSAL_RETURNED_STATUSES: BallStatus[] = [
  'proposal_returned',
  'under_review',
  'accepted',
  'accepted_provisional',
  'returned_for_revision',
  'reconciled',
];

/** Derive the movement direction from a Ball status */
export function ballDirection(status: BallStatus): BallDirection {
  switch (status) {
    case 'draft':
    case 'sent':
    case 'opened':
    case 'gathering_input':
    case 'in_local_deliberation':
    case 'proposal_drafted':
      return 'downward';
    case 'proposal_returned':
    case 'under_review':
    case 'accepted':
    case 'accepted_provisional':
    case 'reconciled':
      return 'upward';
    case 'returned_for_revision':
      return 'returned';
    case 'closed':
      return 'closed';
  }
}

/** True if the Ball has a proposal ready for executive review */
export function ballHasReturnedProposal(ball: CatchballBall): boolean {
  return PROPOSAL_RETURNED_STATUSES.includes(ball.ballStatus);
}

/**
 * Child balls that count as resolved for the parent pass-up gate
 * (`catchball_children_all_resolved`, migration 609).
 */
export const CHILD_RESOLVED_STATUSES: BallStatus[] = [
  'closed',
  'reconciled',
  'accepted',
  'accepted_provisional',
];

export function ballChildIsResolved(ball: CatchballBall): boolean {
  return CHILD_RESOLVED_STATUSES.includes(ball.ballStatus);
}

/** Active (in-flight) child — still blocks parent pass-up. */
export function ballChildIsBlocking(ball: CatchballBall): boolean {
  return !ballChildIsResolved(ball);
}

// ── While the ball is away (migration 880, roadmap C-3) ──────────────────────

/** Sent (not opened yet) · With them (opened, being worked) · Overdue. */
export type BallWaitingState = (typeof BALL_WAITING_STATE__VALUES)[number];

/** Statuses in which the ball is still with the people it was sent to. */
const WAITING_STATUSES: ReadonlySet<BallStatus> = new Set<BallStatus>([
  'sent',
  'opened',
  'gathering_input',
  'in_local_deliberation',
  'proposal_drafted',
  'returned_for_revision',
]);

export const BALL_WAITING_STATE_LABELS: Record<BallWaitingState, string> = {
  sent: 'Sent',
  with_them: 'With them',
  overdue: 'Overdue',
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** A local calendar day as a day number, so differences are whole days. */
function localDayNumber(d: Date): number {
  return Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DAY_MS);
}

export interface BallWaiting {
  state: BallWaitingState;
  /** Days since sent (sent), since opened (with_them), or since the due date (overdue). */
  days: number;
  /** "Sent · 2 days" */
  label: string;
  /** Who has it: the named person, else the org unit it was sent to. */
  holder: string | null;
}

// reuse-ack: mirrors public.catchball_ball_waiting_state (supabase/migrations/880_while_the_ball_is_away.sql) — the database decides who is nudged with it; the inbox rows draw it from balls already loaded, without a round trip per row.
/**
 * Where a sent ball stands while it is away. Null when it is not waiting on
 * anyone below (draft, or already back up / resolved). The due date is a
 * calendar day (src/lib/calendarDate.ts): overdue starts the day after it.
 */
export function ballWaitingState(ball: CatchballBall, now: Date = new Date()): BallWaiting | null {
  if (!WAITING_STATUSES.has(ball.ballStatus)) return null;
  const today = localDayNumber(now);
  const holder = ball.recipientUserName?.trim() || ball.recipientOrgUnitName?.trim() || null;
  const daysSince = (iso: string | null | undefined): number => {
    if (!iso) return 0;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? 0 : Math.max(0, today - localDayNumber(d));
  };
  const due = parseCalendarDate(ball.dueDate);
  let state: BallWaitingState;
  let days: number;
  if (due && today > localDayNumber(due)) {
    state = 'overdue';
    days = today - localDayNumber(due);
  } else if (ball.ballStatus === 'sent' && !ball.openedAt) {
    state = 'sent';
    days = daysSince(ball.sentAt ?? ball.createdAt);
  } else {
    state = 'with_them';
    days = daysSince(ball.openedAt ?? ball.sentAt ?? ball.createdAt);
  }
  return {
    state,
    days,
    label: `${BALL_WAITING_STATE_LABELS[state]} · ${days} ${days === 1 ? 'day' : 'days'}`,
    holder,
  };
}

/** Status badge variant */
export function ballStatusVariant(
  status: BallStatus
): 'success' | 'info' | 'warning' | 'neutral' | 'muted' {
  switch (status) {
    case 'accepted':
    case 'reconciled':
      return 'success';
    case 'accepted_provisional':
    case 'proposal_returned':
      return 'info';
    case 'returned_for_revision':
    case 'under_review':
      return 'warning';
    case 'draft':
    case 'closed':
      return 'muted';
    default:
      return 'neutral';
  }
}

// ── DB row → domain object ────────────────────────────────────────────────────

export function rowToCatchballBall(row: Record<string, unknown>): CatchballBall {
  return {
    id:                    row.id as string,
    loopId:                row.loop_id as string,
    cycleId:               (row.cycle_id as string | null) ?? null,
    workspaceId:           (row.workspace_id as string | null) ?? null,
    name:                  row.name as string,
    recipientOrgUnitId:    (row.recipient_org_unit_id as string | null) ?? null,
    recipientOrgUnitName:  (row.recipient_org_unit_name as string | null) ?? null,
    recipientUserId:       (row.recipient_user_id as string | null) ?? null,
    recipientUserName:     (row.recipient_user_name as string | null) ?? null,
    requestText:           (row.request_text as string | null) ?? null,
    requestInstructions:   (row.request_instructions as string | null) ?? null,
    requestConstraints:    (row.request_constraints as string | null) ?? null,
    requestEvidenceNotes:  (row.request_evidence_notes as string | null) ?? null,
    dueDate:               (row.due_date as string | null) ?? null,
    ballStatus:            (row.ball_status as BallStatus) ?? 'draft',
    parentBallId:          (row.parent_ball_id as string | null) ?? null,
    rootBallId:            (row.root_ball_id as string | null) ?? null,
    depth:                 (row.depth as number) ?? 0,
    rollupAggregatedAt:    (row.rollup_aggregated_at as string | null) ?? null,
    trackId:               (row.track_id as string | null) ?? null,
    receivedContent:       (row.received_content as Record<string, unknown> | null) ?? null,
    passedContent:         (row.passed_content as Record<string, unknown> | null) ?? null,
    sentAt:                (row.sent_at as string | null) ?? null,
    sentByUserId:          (row.sent_by_user_id as string | null) ?? null,
    openedAt:              (row.opened_at as string | null) ?? null,
    proposalSubmittedAt:   (row.proposal_submitted_at as string | null) ?? null,
    proposalAcceptedAt:    (row.proposal_accepted_at as string | null) ?? null,
    lastActionAt:          (row.last_action_at as string) ?? new Date().toISOString(),
    sequenceInLoop:        (row.sequence_in_loop as number) ?? 1,
    createdAt:             row.created_at as string,
    createdBy:             (row.created_by as string | null) ?? null,
    updatedAt:             row.updated_at as string,
    // View extras (cycleId is mapped above with the core fields)
    loopState:             (row.loop_state as string | undefined),
    loopIntent:            row.loop_intent,
  };
}

// ── Create payload ────────────────────────────────────────────────────────────

export interface CreateBallPayload {
  loopId: string;
  workspaceId?: string | null;
  name: string;
  recipientOrgUnitId?: string | null;
  recipientOrgUnitName?: string | null;
  recipientUserId?: string | null;
  recipientUserName?: string | null;
  requestText?: string | null;
  requestInstructions?: string | null;
  requestConstraints?: string | null;
  requestEvidenceNotes?: string | null;
  dueDate?: string | null;
  trackId?: string | null;
  sequenceInLoop?: number;
  parentBallId?: string | null;
}

export interface UpdateBallPayload {
  name?: string;
  ballStatus?: BallStatus;
  recipientOrgUnitId?: string | null;
  recipientOrgUnitName?: string | null;
  recipientUserId?: string | null;
  recipientUserName?: string | null;
  requestText?: string | null;
  requestInstructions?: string | null;
  requestConstraints?: string | null;
  requestEvidenceNotes?: string | null;
  dueDate?: string | null;
  trackId?: string | null;
  sentAt?: string | null;
  sentByUserId?: string | null;
  openedAt?: string | null;
  proposalSubmittedAt?: string | null;
  proposalAcceptedAt?: string | null;
  passedContent?: Record<string, unknown> | null;
}

/**
 * The root Ball a Cycle Designer launch opens at the path's anchor (canon 29 §3).
 * It holds the request where it starts. It is never sent to anyone, so it is not a
 * branch and returns no Proposal: counting it read "sent to 2 branches" for one
 * recipient, with a branch that never answers.
 */
export function isAnchorBall(ball: Pick<CatchballBall, 'depth' | 'sentAt' | 'ballStatus' | 'recipientUserId'>): boolean {
  return ball.depth === 0 && ball.sentAt == null && ball.recipientUserId == null && ball.ballStatus !== 'draft';
}
